// ** React Imports
import { useContext } from 'react'

//** MUI Imports
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

//** Icon Imports
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'

// ** Third Party Imports
import { useAutoAnimate } from '@formkit/auto-animate/react'

// ** Axios

// ** Hook Imports
import useFetch from 'src/hooks/useFetch'
import useSWR from 'swr'

// ** Context
import { AuthContext } from 'src/context/AuthContext'

// ** Configs
import endPointRiwayat from 'src/configs/endpoints/riwayatHidupEndPoints'

// ** Custom Imports

interface DialogProps {
  open: boolean
  setOpen: (open: boolean) => void
}

interface PelamarResponse {
  success: string
  message: string
  data: {
    id: string
    user_id: string
    nik: string
    nomor_telepon: string
    nama_panggilan: string
    tentang_saya: string
    tempat_lahir: string
    tanggal_lahir: string
    jenis_kelamin: string
    agama: string
    status_perkawinan: string
    kewarganegaraan: string
    alamat_ktp: string
    rtrw_ktp: string
    kelurahan_desa_ktp: string
    kecamatan_ktp: string
    kabupaten_kota_ktp: string
    provinsi_ktp: string
    alamat_domisili: string
    rtrw_domisili: string
    kelurahan_desa_domisili: string
    kecamatan_domisili: string
    kabupaten_kota_domisili: string
    provinsi_domisili: string
    npwp: string
    nomor_bpjs_kesehatan: string
    nomor_bpjs_ketenagakerjaan: string
    nomor_rekening: string
    hobi: string
    tinggi_badan: string
    berat_badan: string
    ukuran_baju: string
    ukuran_celana: string
    ukuran_sepatu: string
    alasan_bergabung: string
    visi_bergabung: string
    misi_bergabung: string
    link_video: string
    file_ktp: string
    file_kk: string
    file_kelulusan: string
    file_nilai_kelulusan: string
    file_skck: string
    file_surat_rekomendasi: string
    file_tambahan: string
    created_at: string
    updated_at: string
    user: {
      id: string
      username: string
      email: string
      user_photo: string
      nama_lengkap: string
      role_id: string
      status_user: string
      alasan_blacklist: string
      created_at: string
      updated_at: string
    }
  }
}

const CustomModalInternal = (props: DialogProps) => {
  const { setOpen, open } = props

  // ** Hooks
  const { FetchWithPayload } = useFetch()

  const { user } = useContext(AuthContext)

  const payloadPelamar = {
    url: `${endPointRiwayat.pelamarGetEndpoint}/${user?.id}`,
    method: 'GET'
  }
  const { data: dataPelamar, isLoading: isLoadingPelamar } = useSWR<PelamarResponse>(
    [`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, payloadPelamar],
    FetchWithPayload
  )

  const animationParent500 = useAutoAnimate({ duration: 500, easing: 'cubic-bezier(0, 1.4, 1, 1)' })

  const handleClose = () => {
    setOpen(!open)
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      sx={{
        '& .MuiPaper-root': {
          p: 8,
          pt: 12,
          px: 12,
          width: '40%',
          minWidth: 400
        }
      }}
    >
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 4,
          top: 4,
          p: 0,
          color: theme => theme.palette.error.main
        }}
      >
        <HighlightOffRoundedIcon />
      </IconButton>
      <Box sx={{ display: 'flex', flexDirection: 'column' }} ref={animationParent500}>
        <Typography variant='h6' sx={{ mb: 4, fontWeight: 600, color: theme => theme.palette.primary.main }}>
          Registrasi Rekrutmen
        </Typography>
        {!isLoadingPelamar && dataPelamar && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              alt=''
              sx={{ mr: 4, width: 56, height: 56, backgroundColor: theme => theme.myColor?.colorToggleLight }}
              src={dataPelamar?.data.user.user_photo}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                {dataPelamar?.data.user.username}
              </Typography>
              <Typography variant='body1'>{dataPelamar?.data.user.nama_lengkap}</Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mb: 4,
            py: 4,
            px: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: theme => theme.palette.error.light,
            borderRadius: 1
          }}
        >
          <ReportProblemRoundedIcon color='error' sx={{ fontSize: '3rem' }} />

          <Typography
            variant='h6'
            sx={{ mb: 4, textAlign: 'center', fontWeight: 600, color: theme => theme.palette.error.main }}
          >
            Hanya Pegawai Internal Bankaltimtara yang dapat mengikuti rekrutmen ini
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button
            variant='contained'
            color='error'
            onClick={handleClose}
            sx={{ mt: 4, mr: 2, borderRadius: 4, textTransform: 'none', width: 'fit-content' }}
          >
            Tutup
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}

export default CustomModalInternal
